<template>
  <div class="children-summary-bar">
    <template v-if="hasChildren">
      <div class="children-summary-bar__info">
        <div class="children-summary-bar__avatars">
          <BAvatar
            v-for="child in visibleAvatarChildren"
            :key="child.id"
            size="32"
            :src="child.avatar"
            :text="avatarText(child.full_name)"
            class="children-summary-bar__avatar"
          />
          <div
            v-if="extraAvatarsCount > 0"
            class="children-summary-bar__avatar children-summary-bar__avatar--extra"
          >
            +{{ extraAvatarsCount }}
          </div>
        </div>

        <p class="children-summary-bar__text">
          <span class="children-summary-bar__text-bold">{{ summaryTitle }}</span>
          <span class="children-summary-bar__text-names"> {{ summaryNames }}</span>
        </p>
      </div>

      <div class="children-summary-bar__actions">
        <router-link
          :to="{ name: 'adding-new-child' }"
          class="children-summary-bar__view-all"
        >{{ viewAllButtonText }}</router-link>

        <button
          type="button"
          class="btn btn--color-outline-dark children-summary-bar__add-btn"
          @click="openAddChildModal"
        >
          <span class="capitalize">+ {{ addChildButtonText }}</span>
        </button>
      </div>
    </template>

    <template v-else>
      <p class="children-summary-bar__text children-summary-bar__text--empty">
        {{ noChildrenText }}
      </p>

      <button
        type="button"
        class="btn btn--color-outline-dark children-summary-bar__add-btn"
        @click="openAddChildModal"
      >
        <span class="capitalize">+ {{ addChildButtonText }}</span>
      </button>
    </template>

    <AddNewChildModal
      :is-show-modal="isShowAddChildModal"
      :default-gender="gender"
      :family-name="familyName"
      :family-id="familyId"
      @closeModal="closeAddChildModal"
      @updateChildrenList="onChildAdded"
    />
  </div>
</template>

<script>
import { BAvatar } from 'bootstrap-vue'
import { mapGetters } from 'vuex'

import { instanceSessionCamperNames } from '@core/mixins/instanceSessionCamperNames'
import { prepareProgramRegistrationInfo } from '@core/mixins/prepareProgramRegistrationInfo'
import { getUserData } from '@/auth/utils'
import store from '@/store'
import AddNewChildModal from '@/components/site/add-new-child-modal/AddNewChildModal.vue'

const MAX_VISIBLE_AVATARS = 4
const MAX_VISIBLE_NAMES = 3

export default {
  name: 'ChildrenSummaryBar',
  components: {
    BAvatar,
    AddNewChildModal,
  },
  mixins: [instanceSessionCamperNames, prepareProgramRegistrationInfo],
  data: () => ({
    gender: getUserData()?.gender,
    familyName: getUserData()?.last_name,
    familyId: getUserData()?.family_id,
    isShowAddChildModal: false,
  }),
  computed: {
    ...mapGetters({
      authSettings: 'authSettings/getAuthSettings',
      childrenList: 'children/getChildrenList',
      hasChildrenLoaded: 'children/hasChildrenLoaded',
    }),
    hasChildren() {
      return this.childrenList.length > 0
    },
    visibleAvatarChildren() {
      return this.childrenList.slice(0, MAX_VISIBLE_AVATARS)
    },
    extraAvatarsCount() {
      return Math.max(0, this.childrenList.length - MAX_VISIBLE_AVATARS)
    },
    visibleNames() {
      return this.childrenList.slice(0, MAX_VISIBLE_NAMES).map(child => this.firstName(child.full_name))
    },
    extraNamesCount() {
      return Math.max(0, this.childrenList.length - MAX_VISIBLE_NAMES)
    },
    summaryTitle() {
      return this.getCustomText(
        this.authSettings,
        'children_summary_bar_title',
        '{count} {camperStudent} on your account:',
        {
          count: this.childrenList.length,
          camperStudent: this.camperStudent('plural'),
        },
      )
    },
    summaryNames() {
      if (this.extraNamesCount > 0) {
        return this.getCustomText(
          this.authSettings,
          'children_summary_bar_names_with_more',
          '{names} and {extra} more',
          {
            names: this.visibleNames.join(', '),
            extra: this.extraNamesCount,
          },
        )
      }

      return this.visibleNames.join(', ')
    },
    noChildrenText() {
      return this.getCustomText(
        this.authSettings,
        'children_summary_bar_empty_text',
        'No {camperStudent} on your account yet. Add each {camperStudentSingular} once and use them for every program.',
        {
          camperStudent: this.camperStudent('plural'),
          camperStudentSingular: this.camperStudent('singular'),
        },
      )
    },
    viewAllButtonText() {
      return this.getCustomText(this.authSettings, 'children_summary_bar_view_all_button', 'View all')
    },
    addChildButtonText() {
      return this.getCustomText(
        this.authSettings,
        'application_select_child_add_button',
        'Add A {camperStudent}',
        { camperStudent: this.camperStudent('singular') },
      )
    },
  },
  async created() {
    if (!this.hasChildrenLoaded) {
      await store.dispatch('children/fetchChildrenList', {})
    }
  },
  methods: {
    firstName(fullName) {
      if (!fullName) return ''
      return fullName.split(' ')[0]
    },
    avatarText(value) {
      if (!value) return ''
      return value.split(' ').map(word => word.charAt(0).toUpperCase()).join('')
    },
    openAddChildModal() {
      this.isShowAddChildModal = true
    },
    closeAddChildModal() {
      this.isShowAddChildModal = false
    },
    async onChildAdded() {
      await store.dispatch('children/fetchChildrenList', {})
      this.closeAddChildModal()
    },
  },
}
</script>

<style lang="scss" scoped>

</style>


